/**
 * Client-side filter engine for the unified list-filters program (spec 2026-07-22).
 *
 * CRM filters in SQL via `FilterBinder.cs`. Tax-lifecycle and supervision filter client-side, so
 * they need a pure JS function that mirrors FilterBinder's semantics operator-for-operator against
 * an in-memory array. It is a FAITHFUL, LITERAL port of FilterBinder.cs, pinned by exhaustive unit
 * tests.
 *
 * 🔑 **ONE ENGINE, TWO BYTE-IDENTICAL COPIES (BL-141, 2026-10-21).** `ptc-supervision` and
 * `ptc-tax-lifecycle` carry this file byte for byte — the 337-line fork the fleet filter contract
 * measured is gone. **Do not edit one copy.** Change both in the same pair of PRs, and change the
 * fleet fixture (`ptc-docs/coordination/fixtures/filter-conformance-cases.json`) first when the
 * change is a semantic one: each repo's `filterConformance.test.ts` runs this engine against it, and
 * that is the only check that notices two copies drifting apart while both suites stay green.
 *
 * Contract (all carried verbatim from FilterBinder.cs / the program spec):
 *
 *  - **Forgiving parse.** Unknown column, unknown op for the column's type, or unparseable value
 *    is silently skipped (that one filter is dropped, never thrown). A stale/hand-edited URL
 *    degrades to a broader list rather than an error.
 *  - **NULL-keeping exclusion.** Negative ops (`ne`, `notContains`, `notIn`) compose as
 *    `col == null || !(predicate)` — excluding a value never hides rows that lack the dimension.
 *  - **Whole-day date bounds (Q3 / BL-142).** A bare date — an instant at UTC-midnight, with no
 *    time-of-day — spans its whole day for `eq`, `ne`, `gt`, `lte` and `between`'s upper bound.
 *    A value carrying a time is honoured EXACTLY, by the same helper and the same test, so the two
 *    behaviours are one rule rather than a special case. `gte` and `lt` are deliberately NOT
 *    extended: for a bare date they already mean what a human means, and extending either would
 *    move the bound a day. Landed here 2026-10-20; `ptc-core`'s FilterBinder landed the same
 *    semantics the same day, independently.
 *  - **Dynamic date tokens.** Values are run through the shared {@link resolveViewQuery} resolver
 *    (`{{today}}`, `{{weekStart}}`, …) before application.
 *  - **AND across columns, one condition per column** (v1) — the caller's filter record already
 *    holds at most one `filter.<col>.<op>` per column.
 *
 * Case semantics mirror production CRM (SQL Server's case-insensitive collation): every TEXT op,
 * `eq`/`ne` included, is case-INSENSITIVE (fixture: `text-eq-case-insensitive`). Picklist codes
 * compare exactly — they are codes, not prose. LIKE wildcards (`% _ [`) match literally — JS
 * substring/prefix/suffix checks already treat them as literals, so no escaping is needed here.
 * *(Until 2026-10-21 this paragraph said text `eq`/`ne` was case-SENSITIVE; the code below it had
 * been case-insensitive since the reconcile with tax-lifecycle's copy.)*
 */
import { FilterType, NO_VALUE_TOKEN } from './columnFilterOps';
import { FilterColumn } from './types';
import { resolveViewQuery } from './viewQuery';

/** `filter.<col>.<op>` — col may itself contain dots; the LAST segment is the op. */
const FILTER_KEY_PATTERN = /^filter\.(.+)\.([^.]+)$/;

/** Valid operators per filterType (superset of the UI menu, matching FilterBinder's per-kind ops).
 * `in`/`notIn` on `boolean` (fleet filter contract v3 §16.8 / C86, 2026-11-02): the lookup combobox
 * covers boolean columns too, not just picklist — see booleanPredicate's own `in`/`notIn` cases. */
const VALID_OPS: Record<FilterType, ReadonlySet<string>> = {
  text: new Set(['contains', 'notContains', 'startsWith', 'endsWith', 'eq', 'ne', 'containsData', 'noData']),
  'date-range': new Set(['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'between', 'containsData', 'noData']),
  number: new Set(['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'between', 'containsData', 'noData']),
  picklist: new Set(['in', 'notIn', 'eq', 'ne', 'containsData', 'noData']),
  boolean: new Set(['in', 'notIn', 'eq', 'ne', 'containsData', 'noData']),
};

/** Parse a filter record key into `{ col, op }`, or null if it isn't a `filter.*` key. */
export function parseFilterKey(key: string): { col: string; op: string } | null {
  const m = FILTER_KEY_PATTERN.exec(key);
  return m ? { col: m[1], op: m[2] } : null;
}

// ---------- value coercion (unparseable → null/false so the filter is skipped) ----------

function asStringOrNull(v: unknown): string | null {
  if (v === null || v === undefined) return null;
  if (typeof v === 'string') return v;
  return String(v);
}

function asNumberOrNull(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') return Number.isNaN(v) ? null : v;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

function asBoolOrNull(v: unknown): boolean | null {
  if (v === null || v === undefined) return null;
  if (typeof v === 'boolean') return v;
  if (v === 'true') return true;
  if (v === 'false') return false;
  return null;
}

/**
 * Parse a boolean FILTER value the way FilterBinder's `bool.TryParse` does: case-insensitive, with
 * surrounding whitespace ignored, so `True` is true. Row values keep {@link asBoolOrNull}'s exact
 * reading; this is the URL side only (fixture: `boolean-value-parses-like-bool-TryParse`).
 */
function parseBoolFilter(value: string): boolean | null {
  const s = value.trim().toLowerCase();
  if (s === 'true') return true;
  if (s === 'false') return false;
  return null;
}

/**
 * Split an `in`/`notIn` value the way FilterBinder's `ListQuery.Values` does —
 * `Split(',', TrimEntries | RemoveEmptyEntries)` — so `gold, silver` is {gold, silver}, not
 * {gold, " silver"} (fixture: `choice-in-trims-each-entry`).
 */
function splitSet(value: string): string[] {
  return value.split(',').map((s) => s.trim()).filter((s) => s !== '');
}

/**
 * A date-time with NO zone designator (`2026-03-31T08:00:00`). ECMAScript parses that shape as LOCAL
 * time — unlike a bare date (`2026-03-31`), which it parses as UTC. On a staff machine (ET) the bound
 * `between …T08:00:00` therefore became 12:00Z and let a 09:00Z row in (SUP-001, measured
 * 2026-10-20 by the fleet conformance fixture). The API's TryDate is invariant-UTC, so the client must
 * be too: a zoneless date-time is read as UTC, explicitly.
 */
const ZONELESS_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,7})?)?$/;

/** Parse a filter/row value to epoch-ms, or null on failure. Mirrors TryDate (invariant, UTC). */
function tryDateMs(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v.getTime();
  const s = String(v);
  const t = Date.parse(ZONELESS_DATETIME.test(s) ? `${s}Z` : s);
  return Number.isNaN(t) ? null : t;
}

/** parse a number filter value; NaN → null (unparseable ⇒ filter skipped). */
function tryNumber(s: string): number | null {
  if (s.trim() === '') return null;
  const n = Number(s);
  return Number.isNaN(n) ? null : n;
}

/**
 * Whole-day extension for a date upper bound: if the parsed instant sits at UTC-midnight (no
 * time-of-day — a bare date), extend to the last ms of that day; otherwise honour it exactly.
 * Mirrors FilterBinder's `if (b.TimeOfDay == TimeSpan.Zero) b = b.AddDays(1).AddTicks(-1)`.
 */
function inclusiveUpper(ms: number): number {
  const d = new Date(ms);
  const atMidnight =
    d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0 && d.getUTCMilliseconds() === 0;
  return atMidnight ? ms + 24 * 60 * 60 * 1000 - 1 : ms;
}

// ---------- per-kind predicate builders. Each returns a row-predicate, or null to SKIP. ----------

function textPredicate<T>(get: (i: T) => string | null, op: string, value: string): ((i: T) => boolean) | null {
  const needle = value.toLowerCase();
  switch (op) {
    case 'contains':
      return (i) => { const v = get(i); return v != null && v.toLowerCase().includes(needle); };
    case 'notContains':
      return (i) => { const v = get(i); return v == null || !v.toLowerCase().includes(needle); };
    case 'startsWith':
      return (i) => { const v = get(i); return v != null && v.toLowerCase().startsWith(needle); };
    case 'endsWith':
      return (i) => { const v = get(i); return v != null && v.toLowerCase().endsWith(needle); };
    // Case-INSENSITIVE equality — mirrors production CRM (SQL Server default CI
    // collation via FilterBinder's `v == value`) and tax-lifecycle's engine, so
    // "reviewer equals emma" matches "Emma". (Reconciled across the three apps.)
    case 'eq':
      return (i) => { const v = get(i); return v != null && v.toLowerCase() === needle; };
    case 'ne':
      return (i) => { const v = get(i); return v == null || v.toLowerCase() !== needle; };
    // For text, empty string counts as "no data" (FilterBinder ApplyText).
    case 'containsData':
      return (i) => { const v = get(i); return v != null && v !== ''; };
    case 'noData':
      return (i) => { const v = get(i); return v == null || v === ''; };
    default:
      return null;
  }
}

function numberPredicate<T>(get: (i: T) => number | null, op: string, value: string): ((i: T) => boolean) | null {
  if (op === 'containsData') return (i) => get(i) != null;
  if (op === 'noData') return (i) => get(i) == null;
  if (op === 'between') {
    const parts = value.split('..');
    if (parts.length !== 2) return null;
    const a = tryNumber(parts[0]);
    const b = tryNumber(parts[1]);
    if (a === null || b === null) return null;
    return (i) => { const v = get(i); return v != null && v >= a && v <= b; };
  }
  const n = tryNumber(value);
  if (n === null) return null; // unparseable ⇒ skip
  switch (op) {
    case 'eq': return (i) => get(i) === n;
    case 'ne': return (i) => { const v = get(i); return v == null || v !== n; };
    case 'gt': return (i) => { const v = get(i); return v != null && v > n; };
    case 'gte': return (i) => { const v = get(i); return v != null && v >= n; };
    case 'lt': return (i) => { const v = get(i); return v != null && v < n; };
    case 'lte': return (i) => { const v = get(i); return v != null && v <= n; };
    default: return null;
  }
}

function datePredicate<T>(get: (i: T) => number | null, op: string, value: string): ((i: T) => boolean) | null {
  if (op === 'containsData') return (i) => get(i) != null;
  if (op === 'noData') return (i) => get(i) == null;
  if (op === 'between') {
    const parts = value.split('..');
    if (parts.length !== 2) return null;
    const a = tryDateMs(parts[0]);
    const bRaw = tryDateMs(parts[1]);
    if (a === null || bRaw === null) return null;
    const b = inclusiveUpper(bRaw);
    return (i) => { const v = get(i); return v != null && v >= a && v <= b; };
  }
  const t = tryDateMs(value);
  if (t === null) return null;
  switch (op) {
    // BL-142 / Q3: `eq`, `lte` and `gt` on a date are WHOLE-DAY, consistent with `between`.
    //
    // 🔑 The whole-day-ness rides entirely on `inclusiveUpper`, which extends an instant ONLY when
    // it sits at UTC-midnight — i.e. only when the value was a bare date with no time-of-day. So a
    // value that carries a time is still honoured exactly, and these ops keep their exact-instant
    // behaviour for it. One helper, one rule, and `between` already used it.
    //
    // Why this mattered enough to change: Part I of the fleet contract left `eq`/`lte` OUT of the
    // date operator menu, so exact-instant was unreachable except by hand-editing a URL. v2 §12
    // puts them in the menu verbatim from Dataverse — "On" and "On or before" — one click from
    // every user, where "on or before the 31st" excluding the whole 31st is not what the words
    // mean in any language and the operator has no way to tell.
    case 'eq': return (i) => { const v = get(i); return v != null && v >= t && v <= inclusiveUpper(t); };

    // `ne` MUST stay the exact complement of `eq`, or a bare date would match both. The fixture
    // does not cover this — it has no date `ne` case — so it is derived from §4.3 rather than
    // pinned by one, and the NULL-keeping clause is why: negative ops compose as
    // `col == null || !(predicate)`, so a row with no date is excluded by `eq` and KEPT by `ne`.
    case 'ne': return (i) => { const v = get(i); return v == null || !(v >= t && v <= inclusiveUpper(t)); };

    // "After the 31st" must exclude the whole of the 31st, not just its first instant.
    case 'gt': return (i) => { const v = get(i); return v != null && v > inclusiveUpper(t); };

    // `gte` and `lt` need no extension and must NOT get one. For a bare date they already mean
    // what a human means: "on or after the 31st" starts at the 31st's midnight, and "before the
    // 31st" ends there. Extending either would move the bound a day.
    case 'gte': return (i) => { const v = get(i); return v != null && v >= t; };
    case 'lt': return (i) => { const v = get(i); return v != null && v < t; };

    case 'lte': return (i) => { const v = get(i); return v != null && v <= inclusiveUpper(t); };
    default: return null;
  }
}

/**
 * `in`/`notIn` over a code set that may ALSO contain {@link NO_VALUE_TOKEN} (fleet filter
 * contract v3 §16.8 / C86): the lookup combobox's "(has no value)" checkbox, folded into the same
 * value list rather than a separate operator. Additive-only — a value list that never carries the
 * token behaves exactly as `in`/`notIn` always did (verified by the pre-existing fixture cases,
 * which carry no such token).
 *
 *   in,    no token:  v != null && codes.includes(v)                      -- unchanged
 *   in,    token:      v == null ? true  : codes.includes(v)              -- null rows now match too
 *   notIn, no token:   v == null || !codes.includes(v)                    -- unchanged
 *   notIn, token:      v == null ? false : !codes.includes(v)             -- null rows now EXCLUDED
 *
 * C86's own wording: "choosing two options and toggling Exclude lists every row whose value is
 * neither (rows with no value included UNLESS '(has no value)' is also chosen)" — notIn's default
 * (no token) keeps null rows; adding the token is what excludes them.
 */
function setPredicate<T, V>(
  get: (i: T) => V | null,
  op: 'in' | 'notIn',
  value: string,
  parse: (code: string) => V | undefined
): ((i: T) => boolean) | null {
  const raw = splitSet(value);
  if (raw.length === 0) return null; // empty set ⇒ skip (FilterBinder returns q unchanged)
  const includeEmpty = raw.includes(NO_VALUE_TOKEN);
  const codes = new Set(
    raw.filter((s) => s !== NO_VALUE_TOKEN).map(parse).filter((v): v is V => v !== undefined)
  );
  if (op === 'in') {
    return (i) => { const v = get(i); return v == null ? includeEmpty : codes.has(v); };
  }
  return (i) => { const v = get(i); return v == null ? !includeEmpty : !codes.has(v); };
}

function picklistPredicate<T>(get: (i: T) => string | null, op: string, value: string): ((i: T) => boolean) | null {
  switch (op) {
    case 'in':
    case 'notIn':
      return setPredicate(get, op, value, (code) => code);
    case 'eq': return (i) => get(i) === value;
    case 'ne': return (i) => { const v = get(i); return v == null || v !== value; };
    case 'containsData': return (i) => get(i) != null;
    case 'noData': return (i) => get(i) == null;
    default: return null;
  }
}

function booleanPredicate<T>(get: (i: T) => boolean | null, op: string, value: string): ((i: T) => boolean) | null {
  if (op === 'containsData') return (i) => get(i) != null;
  if (op === 'noData') return (i) => get(i) == null;
  // C86: boolean is a lookup column too (exactly {true, false}) — same combobox, same `in`/`notIn`
  // set semantics as picklist, over the string codes 'true'/'false'.
  if (op === 'in' || op === 'notIn') {
    return setPredicate(get, op, value, (code) => (code === 'true' ? true : code === 'false' ? false : undefined));
  }
  const b = parseBoolFilter(value);
  if (b === null) return null; // unparseable ⇒ skip (mirrors bool.TryParse guard)
  switch (op) {
    case 'eq': return (i) => get(i) === b;
    case 'ne': return (i) => { const v = get(i); return v == null || v !== b; };
    default: return null;
  }
}

/** Build a single column filter's predicate, or null to skip (forgiving grammar). */
function buildPredicate<T>(col: FilterColumn<T>, op: string, value: string): ((i: T) => boolean) | null {
  const raw = col.getValue ?? (() => undefined);
  switch (col.filterType) {
    case 'text':
      return textPredicate((i: T) => asStringOrNull(raw(i)), op, value);
    case 'number':
      return numberPredicate((i: T) => asNumberOrNull(raw(i)), op, value);
    case 'date-range':
      return datePredicate((i: T) => tryDateMs(raw(i)), op, value);
    case 'picklist':
      return picklistPredicate((i: T) => asStringOrNull(raw(i)), op, value);
    case 'boolean':
      return booleanPredicate((i: T) => asBoolOrNull(raw(i)), op, value);
    default:
      return null;
  }
}

/**
 * Apply a flat `filter.<col>.<op>` record to `items`, returning a new filtered array. Columns AND
 * together; unknown columns/ops and unparseable values are silently skipped. `now` seeds dynamic
 * date-token resolution (injectable for tests).
 */
export function applyClientFilters<T>(
  items: T[],
  filters: Record<string, string>,
  columns: FilterColumn<T>[],
  now: Date = new Date()
): T[] {
  const resolved = resolveViewQuery(filters, now);
  const byKey = new Map(columns.map((c) => [c.key, c]));

  let result = items;
  for (const [key, value] of Object.entries(resolved)) {
    const parsed = parseFilterKey(key);
    if (!parsed) continue;
    const col = byKey.get(parsed.col);
    if (!col || !col.filterType) continue; // unknown column ⇒ skip
    if (!VALID_OPS[col.filterType].has(parsed.op)) continue; // unknown op ⇒ skip
    const pred = buildPredicate(col, parsed.op, value);
    if (!pred) continue; // unparseable value ⇒ skip
    result = result.filter(pred);
  }
  return result;
}
